import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useReducer, useRef, } from "react";
const initialState = {
    isAuthenticated: false,
    isLoading: true,
    isInitializing: true,
    user: null,
    error: null,
    sessionExpiresAt: null,
};
function authReducer(state, action) {
    switch (action.type) {
        case "LOADING":
            return { ...state, isLoading: true, error: null };
        case "INITIALIZED":
            return { ...state, isInitializing: false };
        case "AUTHENTICATED":
            return {
                isAuthenticated: true,
                isLoading: false,
                isInitializing: false,
                user: action.user,
                error: null,
                sessionExpiresAt: action.expiresAt,
            };
        case "UNAUTHENTICATED":
            return { ...initialState, isLoading: false, isInitializing: false };
        case "ERROR":
            return { ...state, isLoading: false, isInitializing: false, error: action.error };
        default:
            return state;
    }
}
function sessionToUser(session) {
    return {
        userId: session.userId,
        email: session.email,
        emailVerified: session.emailVerified,
        givenName: session.givenName,
        familyName: session.familyName,
        activeOrgId: session.activeOrgId,
        orgIds: session.orgIds,
        role: session.role,
        permissions: session.permissions,
        entitlements: session.entitlements,
        isImpersonating: session.isImpersonating,
        impersonatedBy: session.impersonatedBy,
        impersonationSessionId: session.impersonationSessionId,
    };
}
const AuthContext = createContext(null);
export function AuthProvider({ children, config }) {
    const [state, dispatch] = useReducer(authReducer, initialState);
    const callbackHandledRef = useRef(false);
    const sessionExpiresAtRef = useRef(null);
    const fetchSession = useCallback(async () => {
        const response = await fetch(`${config.apiBaseUrl}/v1/auth/session`, {
            credentials: "include",
        });
        if (!response.ok) {
            throw new Error("Failed to fetch session");
        }
        return response.json();
    }, [config.apiBaseUrl]);
    const handleAuthCallback = useCallback(async () => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get("code");
        if (!code) {
            return false;
        }
        // Prevent double-processing in React StrictMode (OAuth codes are single-use)
        if (callbackHandledRef.current) {
            return true;
        }
        callbackHandledRef.current = true;
        // Clear the code from URL immediately to prevent issues with back/forward navigation
        window.history.replaceState(null, "", window.location.pathname);
        try {
            const redirectUri = `${window.location.origin}/callback`;
            const response = await fetch(`${config.apiBaseUrl}/v1/auth/session`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    code,
                    redirectUri,
                }),
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message ?? "Failed to create session");
            }
            const data = await response.json();
            dispatch({
                type: "AUTHENTICATED",
                user: sessionToUser(data.session),
                expiresAt: data.session.expiresAt,
            });
            return true;
        }
        catch (error) {
            callbackHandledRef.current = false; // Allow retry on error
            dispatch({ type: "ERROR", error: error });
            return true;
        }
    }, [config.apiBaseUrl]);
    const checkExistingSession = useCallback(async () => {
        try {
            const sessionResponse = await fetchSession();
            if (sessionResponse.authenticated && sessionResponse.session) {
                dispatch({
                    type: "AUTHENTICATED",
                    user: sessionToUser(sessionResponse.session),
                    expiresAt: sessionResponse.session.expiresAt,
                });
                return true;
            }
            return false;
        }
        catch {
            return false;
        }
    }, [fetchSession]);
    const refreshSession = useCallback(async () => {
        try {
            const response = await fetch(`${config.apiBaseUrl}/v1/auth/session/refresh`, {
                method: "POST",
                credentials: "include",
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.session) {
                    dispatch({
                        type: "AUTHENTICATED",
                        user: sessionToUser(data.session),
                        expiresAt: data.session.expiresAt,
                    });
                    return;
                }
            }
            dispatch({ type: "UNAUTHENTICATED" });
        }
        catch {
            dispatch({ type: "UNAUTHENTICATED" });
        }
    }, [config.apiBaseUrl]);
    useEffect(() => {
        const init = async () => {
            dispatch({ type: "LOADING" });
            const handled = await handleAuthCallback();
            if (handled) {
                dispatch({ type: "INITIALIZED" });
                return;
            }
            const hasSession = await checkExistingSession();
            if (!hasSession) {
                await refreshSession();
            }
        };
        init();
    }, [handleAuthCallback, checkExistingSession, refreshSession]);
    useEffect(() => {
        sessionExpiresAtRef.current = state.sessionExpiresAt;
    }, [state.sessionExpiresAt]);
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== "visible")
                return;
            const expiresAt = sessionExpiresAtRef.current;
            if (!expiresAt)
                return;
            const msUntilExpiry = expiresAt - Date.now();
            if (msUntilExpiry < 5 * 60 * 1000) {
                refreshSession();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [refreshSession]);
    useEffect(() => {
        if (!state.sessionExpiresAt)
            return;
        const msUntilExpiry = state.sessionExpiresAt - Date.now();
        const msUntilRefresh = msUntilExpiry - 5 * 60 * 1000;
        if (msUntilRefresh <= 0) {
            refreshSession();
            return;
        }
        const timerId = setTimeout(() => {
            refreshSession();
        }, msUntilRefresh);
        return () => clearTimeout(timerId);
    }, [state.sessionExpiresAt, refreshSession]);
    const login = useCallback((options) => {
        const redirectUri = `https://${config.authDomain}/callback`;
        const returnTo = options?.returnTo ?? window.location.href;
        sessionStorage.setItem("auth_return_to", returnTo);
        const params = new URLSearchParams({
            response_type: "code",
            client_id: config.clientId,
            redirect_uri: redirectUri,
            scope: "email openid profile",
        });
        if (options?.identityProvider) {
            params.set("identity_provider", options.identityProvider);
        }
        if (options?.email) {
            params.set("login_hint", options.email);
        }
        const authUrl = `${config.cognitoDomain}/oauth2/authorize?${params.toString()}`;
        window.location.href = authUrl;
    }, [config]);
    const loginWithPassword = useCallback(async (options) => {
        dispatch({ type: "LOADING" });
        try {
            const response = await fetch(`${config.apiBaseUrl}/v1/auth/login`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: options.email,
                    password: options.password,
                }),
            });
            const data = await response.json();
            if (!response.ok) {
                dispatch({ type: "ERROR", error: new Error(data.error ?? "Login failed") });
                return { success: false, error: data.error ?? "Login failed" };
            }
            if (data.challengeName) {
                // MFA or other challenge required
                dispatch({ type: "UNAUTHENTICATED" });
                return {
                    success: false,
                    challengeName: data.challengeName,
                    challengeSession: data.challengeSession,
                };
            }
            if (data.success && data.session) {
                dispatch({
                    type: "AUTHENTICATED",
                    user: sessionToUser(data.session),
                    expiresAt: data.session.expiresAt,
                });
                return { success: true };
            }
            dispatch({ type: "ERROR", error: new Error("Login failed") });
            return { success: false, error: "Login failed" };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Login failed";
            dispatch({ type: "ERROR", error: new Error(errorMessage) });
            return { success: false, error: errorMessage };
        }
    }, [config.apiBaseUrl]);
    const logout = useCallback(async () => {
        sessionStorage.removeItem("auth_return_to");
        // returnTo must be a registered Cognito sign-out URL so destroy-session can
        // build the hosted-UI /logout redirect that clears the Cognito session.
        const returnTo = `https://${config.authDomain}/logout`;
        try {
            const response = await fetch(`${config.apiBaseUrl}/v1/auth/session?returnTo=${encodeURIComponent(returnTo)}`, {
                method: "DELETE",
                credentials: "include",
            });
            if (response.ok) {
                const data = await response.json();
                if (data.logoutUrl) {
                    window.location.href = data.logoutUrl;
                    return;
                }
            }
        }
        catch {
            // Continue with local logout
        }
        dispatch({ type: "UNAUTHENTICATED" });
        window.location.href = `https://${config.authDomain}`;
    }, [config]);
    const value = {
        ...state,
        login,
        loginWithPassword,
        logout,
        refreshSession,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuthContext() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext must be used within an AuthProvider");
    }
    return context;
}
//# sourceMappingURL=auth-context.js.map